import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

export const HlsVideo = forwardRef(({ src, onError, ...props }, forwardedRef) => {
  const videoRef = useRef(null);
  const errorHandlerRef = useRef(onError);
  errorHandlerRef.current = onError;

  useImperativeHandle(forwardedRef, () => videoRef.current);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;
    let hls;
    let fallbackStarted = false;
    let disposed = false;

    const handleError = async () => {
      if (!fallbackStarted) {
        fallbackStarted = true;
        let Hls;
        try {
          ({ default: Hls } = await import("hls.js"));
        } catch {
          if (!disposed) errorHandlerRef.current?.({ currentTarget: video });
          return;
        }
        if (disposed) return;
        if (!Hls.isSupported()) {
          errorHandlerRef.current?.({ currentTarget: video });
          return;
        }
        hls = new Hls();
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) errorHandlerRef.current?.({ currentTarget: video });
        });
        return;
      }
      errorHandlerRef.current?.({ currentTarget: video });
    };

    video.addEventListener("error", handleError);
    return () => {
      disposed = true;
      video.removeEventListener("error", handleError);
      hls?.destroy();
    };
  }, [src]);

  return <video {...props} ref={videoRef} src={src || undefined} />;
});
